import React, { useState, useEffect } from 'react';
import useAccountCreation from '@/hooks/useAccountCreation.js';
import useDropdownData from '@/hooks/useDropdownData.js';
import SearchableDropdown from '@/components/molecules/SearchableDropdown.jsx';
import LoadingSpinner from '@/components/ui/LoadingSpinner.jsx';
import ErrorMessage from '@/components/ui/ErrorMessage.jsx';
import ContactCreationDialog from '@/components/molecules/ContactCreationDialog.jsx';
import CostCenterCreationDialog from '@/components/molecules/CostCenterCreationDialog.jsx'

function AccountCreationForm() {
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isCostCenterDialogOpen, setIsCostCenterDialogOpen] = useState(false);
  const [localContacts, setLocalContacts] = useState([]);
  const [localCostCenters, setLocalCostCenters] = useState([]);

  const {
    formData,
    loading,
    message,
    resetKey,
    updateFormData,
    handleSubmit
  } = useAccountCreation();

  const {
    organizations,
    organizationalUnits,
    types,
    opcos,
    domains,
    contacts,
    costCenters,
    groups,
    loading: dataLoading,
    error: dataError,
    refreshContacts,
    refreshCostCenters
  } = useDropdownData();

  useEffect(() => {
    if (contacts) {
      setLocalContacts(contacts);
    }
  }, [contacts]);

  useEffect(() => {
    if (costCenters) {
      setLocalCostCenters(costCenters);
    }
  }, [costCenters]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleOrganizationSelect = (organization) => {
    updateFormData('organization', organization);
    updateFormData('organizationalUnit', '');
  };

  const handleOrganizationalUnitSelect = (organizationalUnit) => {
    updateFormData('organizationalUnit', organizationalUnit);
  };

  const filteredOrganizationalUnits = (organizationalUnits || []).filter(ou => {
    if (!formData.organization?.CloudOrganizationPublicCloudId) return true;
    return ou.OrganizationalUnitArn?.includes(formData.organization.CloudOrganizationPublicCloudId);
  });

  const handleTypeSelect = (type) => {
    updateFormData('Type', type);
  };

  const handleOpcoSelect = (opco) => {
    updateFormData('Opco', opco);
  };

  const handleDomainSelect = (domain) => {
    updateFormData('Domain', domain);
  };

  const handleAccountOwnerSelect = (contact) => {
    updateFormData('accountOwner', contact);
  };

  const handleAccountableSelect = (contact) => {
    updateFormData('accountable', contact);
  };

  const handleBillingSelect = (contact) => {
    updateFormData('billing', contact);
  };

  const handleOperationsSelect = (contact) => {
    updateFormData('operations', contact);
  };

  const handleCostCenterSelect = (costCenter) => {
    updateFormData('costCenter', costCenter);
  };

  const handleGroupSelect = (group) => {
    updateFormData('group', group);
  };

  const renderContactItem = (contact) => (
    <>
      <div className="font-medium text-gray-900">
        {contact.FirstName} {contact.LastName}
      </div>
      <div className="text-sm text-gray-600">
        {contact.EmailAddress}
      </div>
    </>
  );

  const getContactDisplayValue = (contact) => `${contact.FirstName} ${contact.LastName}`;

  const handleNewContact = () => {
    setIsContactDialogOpen(true);
  };

  const handleNewCostCenter = () => {
    setIsCostCenterDialogOpen(true);
  };

  const handleContactCreated = async (newContact) => {
    setLocalContacts(prev => [...prev, newContact]);
    await refreshContacts();
  };

  const handleCostCenterCreated = async (newCostCenter) => {
    setLocalCostCenters(prev => [...prev, newCostCenter]);
    await refreshCostCenters();
  };

  if (dataLoading) {
    return <LoadingSpinner message="Loading form data..." />;
  }

  if (dataError) {
    return <ErrorMessage error={dataError} showRetry={true} />;
  }

  return (
    <form onSubmit={handleFormSubmit} className="space-y-8">
      {/* Section 1: Account Information */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
          Account Information
        </h3>
        <div className="space-y-6">
          <SearchableDropdown
            key={`org-${resetKey}`}
            items={organizations}
            searchKey="CloudOrganizationUid"
            displayKey="CloudOrganizationName"
            placeholder="Search Organization"
            label="Organization"
            onSelect={handleOrganizationSelect}
            required
          />

          {formData.organization && formData.organization.CloudOrganizationName && (
            <SearchableDropdown
              key={`ou-${resetKey}-${formData.organization?.CloudOrganizationUid || ''}`}
              items={filteredOrganizationalUnits}
              searchKey="OrganizationalUnitPath"
              displayKey="OrganizationalUnitPath"
              placeholder="Search Organizational Unit"
              label="Organizational Unit"
              onSelect={handleOrganizationalUnitSelect}
              required
            />
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Name <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Email <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              IT Demand
            </label>
            <input
              type="text"
              name="itDemand"
              value={formData.itDemand}
              onChange={(e) => updateFormData('itDemand', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              placeholder="Enter IT demand..."
            />
          </div>

          <SearchableDropdown
            key={`type-${resetKey}`}
            items={types}
            searchKey="TypeUid"
            displayKey="TypeName"
            placeholder="Search Type"
            label="Type"
            onSelect={handleTypeSelect}
            required
          />

          <SearchableDropdown
            key={`opco-${resetKey}`}
            items={opcos}
            searchKey="OpcoUid"
            displayKey="OpcoName"
            placeholder="Search Opco"
            label="Opco"
            onSelect={handleOpcoSelect}
            required
          />

          <SearchableDropdown
            key={`domain-${resetKey}`}
            items={domains}
            searchKey="DomainUid"
            displayKey="DomainName"
            placeholder="Search Domain"
            label="Domain"
            onSelect={handleDomainSelect}
            required
          />

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              rows="4"
              placeholder="Enter account description..."
            />
          </div>
        </div>
      </div>

      {/* Section 2: Budget */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
          Budget
        </h3>
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monthly Budget <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="monthlyBudget"
              value={formData.monthlyBudget}
              onChange={(e) => updateFormData('monthlyBudget', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              required
            />
          </div>
        <div className="space-y-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Line Reference
            </label>
            <input
              type="text"
              name="budgetLineReference"
              value={formData.budgetLineReference}
              onChange={(e) => updateFormData('budgetLineReference', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              placeholder="Enter budget line reference..."
            />
          </div>
          <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={handleNewCostCenter}
            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
          >
            New Cost Center
          </button>
        </div>
          <SearchableDropdown
            key={`costCenter-${resetKey}`}
            items={localCostCenters}
            searchKey="Code"
            displayKey="Code"
            placeholder="Search Cost Center"
            label="Cost Center"
            onSelect={handleCostCenterSelect}
            required
          />

          
        </div>
      </div>

      {/* Section 3: Group and Responsibles */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-3">
          Group and Responsibles
        </h3>
        <div className="space-y-6">
          <SearchableDropdown
            key={`group-${resetKey}`}
            items={groups}
            searchKey="AccountsGroupName"
            displayKey="AccountsGroupName"
            placeholder="Search Group"
            label="Group"
            onSelect={handleGroupSelect}
          />
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={handleNewContact}
            className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
          >
            New Contact
          </button>
        </div>
          <SearchableDropdown
            key={`accountOwner-${resetKey}`}
            items={localContacts}
            searchKey="EmailAddress"
            displayKey="FirstName"
            placeholder="Search Account Owner"
            label="Account Owner"
            onSelect={handleAccountOwnerSelect}
            renderItem={renderContactItem}
            getDisplayValue={getContactDisplayValue}
            required
          />

          <SearchableDropdown
            key={`accountable-${resetKey}`}
            items={localContacts}
            searchKey="EmailAddress"
            displayKey="FirstName"
            placeholder="Search Accountable"
            label="Accountable"
            onSelect={handleAccountableSelect}
            renderItem={renderContactItem}
            getDisplayValue={getContactDisplayValue}
            required
          />

          <SearchableDropdown
            key={`billing-${resetKey}`}
            items={localContacts}
            searchKey="EmailAddress"
            displayKey="FirstName"
            placeholder="Search Billing Contact"
            label="Billing"
            onSelect={handleBillingSelect}
            renderItem={renderContactItem}
            getDisplayValue={getContactDisplayValue}
          />

          <SearchableDropdown
            key={`operations-${resetKey}`}
            items={localContacts}
            searchKey="EmailAddress"
            displayKey="FirstName"
            placeholder="Search Operations Contact"
            label="Operations"
            onSelect={handleOperationsSelect}
            renderItem={renderContactItem}
            getDisplayValue={getContactDisplayValue}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <button
          type="submit"
          className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              Creating...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </div>

      {message && (
        <div className="mt-4">
          {message.includes('Error') ? (
            <ErrorMessage error={message} showRetry={false} />
          ) : (
            <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              {message}
            </div>
          )}
        </div>
      )}

      <ContactCreationDialog
        isOpen={isContactDialogOpen}
        onClose={() => setIsContactDialogOpen(false)}
        onContactCreated={handleContactCreated}
      />
      <CostCenterCreationDialog
        isOpen={isCostCenterDialogOpen}
        onClose={() => setIsCostCenterDialogOpen(false)}
        onCostCenterCreated={handleCostCenterCreated}
      />
    </form>
  );
}

export default AccountCreationForm;